/**
 * Project endpoints — workspace projects CRUD + conversation assignment.
 *
 * Projects are the top-level workspace grouping shown in the ``ProjectPill``
 * (top-left breadcrumb). Each project has display metadata (name/icon/color)
 * + default scoping defaults that pre-fill new conversations:
 *
 *   - ``default_folder_filter`` → ``ConversationConfig.folderFilter`` seed
 *   - ``default_connection_ids`` → ``ConversationConfig.selectedConnectionIds``
 *   - ``default_agent_mode_version_id`` → ``ConversationConfig.agentModeVersionId``
 *
 * Wire-format mirrors the BE ``projects`` table (UUID PK, soft delete via
 * ``is_archived``). The full row is returned so the FE can render the pill
 * + dropdown counts without a second fetch.
 *
 * BE source: ``src/api/v1/endpoints/projects.py`` (sibling BE task —
 * mounted at ``/api/v1/projects``).
 */

import type { Project } from "../../domain/project.js";
import { apiClient } from "../ApiClient.js";

/**
 * Workspace project — see ``domain/project.ts::ProjectSchema`` for the zod
 * schema. Re-exported here so callers importing from ``@ando/core/api``
 * (the conventional endpoint barrel) get the type without a second import.
 *
 * ``icon`` is a lucide-react icon name (resolved client-side via the
 * ``LUCIDE_BY_NAME`` map — see ``features/projects/components/ProjectIcon``).
 * ``color`` is an Ando accent hue token (orange/violet/blue/turquoise/pink/
 * green/red/yellow). Null on either field → caller falls back to the
 * brand default (``Folder`` icon + ``ando-orange``).
 */
export type { Project };

export interface CreateProjectInput {
  name: string;
  icon?: string | null;
  color?: string | null;
  description?: string | null;
  default_folder_filter?: readonly string[];
  default_connection_ids?: readonly string[];
  default_agent_mode_version_id?: string | null;
  instructions?: string | null;
  persona_role?: string | null;
  persona_tone?: string | null;
}

export interface UpdateProjectInput {
  name?: string;
  icon?: string | null;
  color?: string | null;
  description?: string | null;
  default_folder_filter?: readonly string[];
  default_connection_ids?: readonly string[];
  default_agent_mode_version_id?: string | null;
  instructions?: string | null;
  persona_role?: string | null;
  persona_tone?: string | null;
  cross_project_transfer_policy?: "isolated" | "shared";
}

// ---------------------------------------------------------------------------
// Phase 5a — intra-org members (June 13, 2026)
// ---------------------------------------------------------------------------

export type ProjectMemberRole = "owner" | "editor" | "viewer";

export interface ProjectMember {
  membership_id: string;
  project_id: string;
  user_id: string;
  tenant_id: string;
  role: ProjectMemberRole;
  added_by_user_id: string | null;
  added_at: string;
}

/**
 * Exactly one of ``user_id`` / ``email`` must be set — mirrors the BE
 * ``InviteProjectMemberRequest`` validator (issue #1103). ``email`` lets the
 * inviter type any address; the BE resolves it to a user_id restricted to
 * its own organization and 404s if no Ando account matches there.
 */
export type InviteProjectMemberInput =
  | { user_id: string; email?: never; role?: Exclude<ProjectMemberRole, "owner"> }
  | { email: string; user_id?: never; role?: Exclude<ProjectMemberRole, "owner"> };

// ---------------------------------------------------------------------------
// Phase 1 sub-resources (June 7, 2026) — per-project artifacts + tasks
// ---------------------------------------------------------------------------

/**
 * Artifact = AI-generated document / deck / sheet (origin_type
 * ``document_fork`` or ``document_create``) scoped to a project.
 * The gallery renders one card per row, newest-first.
 */
export interface ProjectArtifact {
  id: string;
  filename: string | null;
  mime_type: string | null;
  file_size: number | null;
  weaviate_doc_id: string | null;
  origin_type: string;
  origin_id: string | null;
  conversation_id: string | null;
  created_at: string;
}

/** Lightweight task row scoped to a project (priority/due/notes only). */
export interface ProjectTask {
  task_id: string;
  title: string;
  notes: string | null;
  priority: "high" | "normal" | "low";
  due_at: string | null;
  estimated_minutes: number | null;
  completed_at: string | null;
  source: string;
  source_ref: string | null;
  created_at: string;
  updated_at: string;
  project_id: string | null;
}

export interface CreateProjectTaskInput {
  title: string;
  notes?: string | null;
  priority?: "high" | "normal" | "low";
  due_at?: string | null;
  estimated_minutes?: number | null;
}

export interface UpdateProjectTaskInput {
  title?: string;
  notes?: string | null;
  priority?: "high" | "normal" | "low";
  due_at?: string | null;
  estimated_minutes?: number | null;
  /** Toggle completed_at via the dedicated mark/unmark helpers. */
  completed?: boolean;
}

// ---------------------------------------------------------------------------
// Phase 2 — HyperMem per-project memory timeline (June 8, 2026)
// ---------------------------------------------------------------------------

/**
 * Compact summary of a Hive Flow pinned to this project. Mirrors the
 * BE ``ProjectAutomationSummary`` Pydantic model — the per-project
 * Automation pane consumes this projection to avoid fetching the full
 * flow graph until the user actually opens an entry.
 */
export interface ProjectAutomationSummary {
  flow_id: string;
  name: string;
  description: string | null;
  schedule_type: string;
  cron_expression: string | null;
  interval_minutes: number | null;
  timezone: string;
  is_active: boolean;
  status: string;
  last_run_at: string | null;
  next_run_at: string | null;
  total_runs: number;
  created_at: string | null;
  updated_at: string | null;
}

/**
 * One entry on the project memory timeline — an episodic fact the
 * agent has learnt INSIDE this project (general-memory facts that
 * apply across the workspace are intentionally excluded from this
 * surface).
 */
export interface ProjectMemoryEntry {
  fact_id: string;
  fact: string;
  fact_type: string | null;
  confidence: number | null;
  salience_score: number | null;
  mention_count: number | null;
  source_conversation_id: string | null;
  created_at: string;
  last_accessed_at: string | null;
}

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

/** GET ``/projects`` — list non-archived projects for the current org. */
export async function listProjects(): Promise<readonly Project[]> {
  return await apiClient.get<readonly Project[]>("/projects");
}

/** POST ``/projects`` — create a new project. */
export async function createProject(input: CreateProjectInput): Promise<Project> {
  return await apiClient.post<Project>("/projects", input);
}

/** PATCH ``/projects/{id}`` — partial update. */
export async function updateProject(
  projectId: string,
  patch: UpdateProjectInput,
): Promise<Project> {
  return await apiClient.patch<Project>(`/projects/${encodeURIComponent(projectId)}`, patch);
}

/** DELETE ``/projects/{id}`` — soft delete (sets ``is_archived``). */
export async function archiveProject(projectId: string): Promise<void> {
  await apiClient.delete(`/projects/${encodeURIComponent(projectId)}`);
}

/** POST ``/projects/reorder`` — bulk reorder by id list. */
export async function reorderProjects(orderedIds: readonly string[]): Promise<void> {
  await apiClient.post("/projects/reorder", { ordered_ids: orderedIds });
}

/** POST ``/projects/{id}/conversations`` — assign a conversation. */
export async function assignConversationToProject(
  projectId: string,
  conversationId: string,
): Promise<void> {
  await apiClient.post(`/projects/${encodeURIComponent(projectId)}/conversations`, {
    conversation_id: conversationId,
  });
}

/** DELETE ``/projects/{id}/conversations/{conversationId}`` — unassign. */
export async function unassignConversationFromProject(
  projectId: string,
  conversationId: string,
): Promise<void> {
  await apiClient.delete(
    `/projects/${encodeURIComponent(projectId)}/conversations/${encodeURIComponent(conversationId)}`,
  );
}

// ---------------------------------------------------------------------------
// Phase 1 sub-resources (June 7, 2026) — per-project artifacts + tasks
// ---------------------------------------------------------------------------

/** GET ``/projects/{id}/artifacts`` — newest-first artifact gallery feed. */
export async function listProjectArtifacts(
  projectId: string,
  limit = 100,
): Promise<readonly ProjectArtifact[]> {
  return await apiClient.get<readonly ProjectArtifact[]>(
    `/projects/${encodeURIComponent(projectId)}/artifacts?limit=${limit}`,
  );
}

/** GET ``/projects/{id}/tasks`` — per-project task feed (incomplete first). */
export async function listProjectTasks(
  projectId: string,
  options?: { includeCompleted?: boolean; limit?: number },
): Promise<readonly ProjectTask[]> {
  const params = new URLSearchParams();
  if (options?.includeCompleted === false) params.set("include_completed", "false");
  if (options?.limit) params.set("limit", String(options.limit));
  const qs = params.toString();
  return await apiClient.get<readonly ProjectTask[]>(
    `/projects/${encodeURIComponent(projectId)}/tasks${qs ? `?${qs}` : ""}`,
  );
}

/** POST ``/projects/{id}/tasks`` — create a new task scoped to the project. */
export async function createProjectTask(
  projectId: string,
  input: CreateProjectTaskInput,
): Promise<ProjectTask> {
  return await apiClient.post<ProjectTask>(
    `/projects/${encodeURIComponent(projectId)}/tasks`,
    input,
  );
}

/** PATCH ``/projects/{id}/tasks/{taskId}`` — partial update (incl. complete toggle). */
export async function updateProjectTask(
  projectId: string,
  taskId: string,
  patch: UpdateProjectTaskInput,
): Promise<ProjectTask> {
  return await apiClient.patch<ProjectTask>(
    `/projects/${encodeURIComponent(projectId)}/tasks/${encodeURIComponent(taskId)}`,
    patch,
  );
}

/** DELETE ``/projects/{id}/tasks/{taskId}`` — soft delete. */
export async function deleteProjectTask(
  projectId: string,
  taskId: string,
): Promise<void> {
  await apiClient.delete(
    `/projects/${encodeURIComponent(projectId)}/tasks/${encodeURIComponent(taskId)}`,
  );
}

/**
 * GET ``/projects/{id}/memory/timeline`` — newest-first list of episodic
 * facts the agent has learnt INSIDE this project. General-memory facts
 * (those tagged ``project_id IS NULL``) are excluded server-side — the
 * timeline surfaces only project-owned memory.
 */
export async function listProjectMemoryTimeline(
  projectId: string,
  limit = 50,
): Promise<readonly ProjectMemoryEntry[]> {
  return await apiClient.get<readonly ProjectMemoryEntry[]>(
    `/projects/${encodeURIComponent(projectId)}/memory/timeline?limit=${limit}`,
  );
}

/**
 * GET ``/projects/{id}/automations`` — Hive Flows pinned to this project.
 * Phase 3 ecosystem foundation (June 9, 2026). Empty list when no flow
 * has been pinned yet (FE renders its own empty state with a "Crea
 * automazione" CTA that pre-populates ``project_id``).
 */
export async function listProjectAutomations(
  projectId: string,
  limit = 50,
): Promise<readonly ProjectAutomationSummary[]> {
  return await apiClient.get<readonly ProjectAutomationSummary[]>(
    `/projects/${encodeURIComponent(projectId)}/automations?limit=${limit}`,
  );
}

// ---------------------------------------------------------------------------
// Phase 5a — intra-org members API (June 13, 2026)
// ---------------------------------------------------------------------------

/** GET ``/projects/{id}/members`` — owner-first ordering. */
export async function listProjectMembers(
  projectId: string,
): Promise<readonly ProjectMember[]> {
  return await apiClient.get<readonly ProjectMember[]>(
    `/projects/${encodeURIComponent(projectId)}/members`,
  );
}

/** POST ``/projects/{id}/members`` — invite an intra-org user. */
export async function inviteProjectMember(
  projectId: string,
  input: InviteProjectMemberInput,
): Promise<ProjectMember> {
  return await apiClient.post<ProjectMember>(
    `/projects/${encodeURIComponent(projectId)}/members`,
    input,
  );
}

/** PATCH ``/projects/{id}/members/{userId}`` — flip role (editor/viewer). */
export async function updateProjectMemberRole(
  projectId: string,
  memberUserId: string,
  role: Exclude<ProjectMemberRole, "owner">,
): Promise<ProjectMember> {
  return await apiClient.patch<ProjectMember>(
    `/projects/${encodeURIComponent(projectId)}/members/${encodeURIComponent(memberUserId)}`,
    { role },
  );
}

/** DELETE ``/projects/{id}/members/{userId}`` — kick or self-leave. */
export async function removeProjectMember(
  projectId: string,
  memberUserId: string,
): Promise<void> {
  await apiClient.delete(
    `/projects/${encodeURIComponent(projectId)}/members/${encodeURIComponent(memberUserId)}`,
  );
}
